import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { cariTypeDisplay, cariTypeLabel } from './cari-type-display';
import { usePermission } from '@/components/guards/permission-guard';

// Cari type codes are dynamic (admin-managed registry); "CariType" is kept as
// an alias for call sites that still import it.
export type CariType = string;

interface NewCariDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (type: CariType) => void;
}

export function NewCariDialog({ open, onOpenChange, onSelect }: NewCariDialogProps) {
  const { t, i18n } = useTranslation('trading');
  const canCreateCustomer = usePermission('CUSTOMERS', 'CREATE', 'TRADING');
  const canCreateSupplier = usePermission('SUPPLIERS', 'CREATE', 'TRADING');

  const { data: cariTypes } = trpc.tradingCariType.list.useQuery(undefined, { enabled: open });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('cari.selectType')}</DialogTitle>
          <DialogDescription>{t('cari.selectTypeDesc')}</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 pt-2 sm:grid-cols-3 lg:grid-cols-5">
          {(cariTypes ?? []).filter((type) => type.code === 'T' ? canCreateSupplier : canCreateCustomer).map((type) => {
            const display = cariTypeDisplay(type.code);
            const Icon = display.icon;
            return (
              <button
                key={type.id}
                onClick={() => {
                  onSelect(type.code);
                  onOpenChange(false);
                }}
                className={`flex flex-col items-center gap-2 border border-border p-4 transition-all ${display.hoverBorder} hover:shadow-sm cursor-pointer`}
              >
                <div className={`flex h-10 w-10 items-center justify-center ${display.bgColor}`}>
                  <Icon className={`h-5 w-5 ${display.iconColor}`} />
                </div>
                <div className="text-center">
                  <span className="text-xs font-bold text-muted-foreground">{type.code}</span>
                  <p className="text-sm font-medium">{cariTypeLabel(type, i18n.language)}</p>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
